'use client'

import { useCallback, useState } from 'react'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/styles/cn'
import { LandingPageHeroHeader } from '@/features/landing-page/landing-page-hero-header'
import { X, Play } from 'lucide-react'
import { AgentHomeEmptyYoutubeVideoModal } from '@/features/agents/components/agent-home-empty/agent-home-empty-youtube-video-modal'
import { AGENT_INTRO_VIDEO_ID } from '@/features/agents/config/agent-video-config'
import { getLocalStorageItem, setLocalStorageItem, LOCAL_STORAGE_KEYS } from '@/setup/local-storage'

const DEFAULT_GRADIENT_SRC = '/assets/landing-page/onboarding-section/background.webp'

interface IOnboardingSectionProps {
    titleText: string
    subtitleText: string
    onCtaClick: () => void
    theme?: 'default' | 'light'
    className?: string
    ctaLabel?: string
    secondaryCtaLabel?: string
    onSecondaryCtaClick?: () => void
    gradientSrc?: string
    /** Custom content rendered at the bottom of the section, replacing the default "Free to start" text. */
    bottomContent?: React.ReactNode
}

export const OnboardingSection = ({
    titleText,
    subtitleText,
    onCtaClick,
    theme = 'default',
    className,
    ctaLabel = 'Get started free',
    secondaryCtaLabel,
    onSecondaryCtaClick,
    gradientSrc = DEFAULT_GRADIENT_SRC,
    bottomContent,
}: IOnboardingSectionProps) => {
    const isLight = theme === 'light'
    const [videoCardDismissed, setVideoCardDismissed] = useState(
        () => getLocalStorageItem<boolean>(LOCAL_STORAGE_KEYS.Landing.IntroVideoDismissed) === true
    )
    const [videoModalOpen, setVideoModalOpen] = useState(false)

    const dismissVideoCard = useCallback(() => {
        setVideoCardDismissed(true)
        setLocalStorageItem(LOCAL_STORAGE_KEYS.Landing.IntroVideoDismissed, true)
    }, [])

    const handleCtaClick = useCallback(() => {
        onCtaClick()
    }, [onCtaClick])

    return (
        <section className={cn('w-full', className)}>
            <div className="mx-auto w-full max-w-[2400px] px-0">
                <div
                    className={cn(
                        'relative mx-auto w-full max-w-[1408px] bg-white',
                        'rounded-[12px]',
                        'shadow-[0px_1px_2px_rgba(22,21,20,0.06)]',
                        'ring-1 ring-inset ring-[rgba(22,21,20,0.10)]',
                        'overflow-hidden',
                        'min-h-[360px] md:min-h-[480px]',
                        'flex flex-col'
                    )}
                >
                    <div className="absolute inset-0">
                        <Image
                            src={gradientSrc}
                            alt=""
                            fill
                            priority
                            sizes="(max-width: 768px) 100vw, 1408px"
                            className="pointer-events-none select-none object-cover object-[center_90%] md:object-center"
                        />
                    </div>

                    <div className={cn('relative flex h-full w-full flex-col items-center', isLight && 'text-white')}>
                        <div className="w-full px-5 md:px-0 pt-10 sm:pt-12 md:pt-[88px]">
                            <div className="mx-auto flex w-full max-w-[800px] flex-col items-center text-center">
                                <div className="flex w-full flex-col items-center gap-[18px]">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[13px] md:text-[14px] font-normal text-[#858280] leading-[1.4]">
                                            Featured on
                                        </span>
                                        <Image
                                            src="/assets/landing-page/onboarding-section/hubspot-podcast-logo.png"
                                            alt="HubSpot"
                                            width={64}
                                            height={19}
                                            className="h-[16px] w-auto"
                                        />
                                        <span className="text-[13px] md:text-[14px] font-normal text-[#858280] leading-[1.4]">
                                            podcast
                                        </span>
                                    </div>

                                    <div className="w-full">
                                        <LandingPageHeroHeader
                                            as="h1"
                                            titleText={titleText}
                                            subtitleText={subtitleText}
                                            variant={isLight ? 'dark' : 'light'}
                                            className="overflow-visible gap-[18px]"
                                            titleClassName="max-w-[920px] w-full px-1"
                                            subtitleClassName={isLight ? 'text-white/85' : 'text-[#838383]'}
                                        />
                                    </div>
                                </div>

                                <div className="mt-[32px] flex w-full flex-col items-center justify-center gap-3 sm:flex-row">
                                    <Button
                                        variant="ds2-primary"
                                        size="ds2-lg"
                                        data-cta="get_started"
                                        data-section="hero"
                                        onClick={handleCtaClick}
                                        className="relative z-10 px-4"
                                        childContainerClassName="relative z-10"
                                    >
                                        <span className="ds-body font-medium text-[16px] leading-[19px] text-white">
                                            {ctaLabel}
                                        </span>
                                    </Button>

                                    {secondaryCtaLabel && onSecondaryCtaClick && (
                                        <Button
                                            variant="ds2-secondary"
                                            size="ds2-lg"
                                            onClick={onSecondaryCtaClick}
                                            className="relative z-10 px-4 bg-white/70 backdrop-blur-md"
                                        >
                                            <span className="ds-body font-medium text-[16px] leading-[19px]">
                                                {secondaryCtaLabel}
                                            </span>
                                        </Button>
                                    )}
                                </div>

                                <div
                                    className={cn(
                                        'mt-[24px] sm:mt-[36px] mb-[16px] md:hidden text-[13px] md:text-[14px] leading-[1.4] font-normal text-center',
                                        isLight ? 'text-white/60' : 'text-[#16151466]'
                                    )}
                                >
                                    {bottomContent ?? 'Free to start · No credit card · First results in minutes'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop bottom row: text + video card, vertically centered */}
                    <div className="relative z-20 hidden md:flex items-center justify-between w-full px-5 py-6 mt-auto">
                        <div className="flex-1" />
                        <div
                            className={cn(
                                'text-[14px] leading-[1.4] font-normal text-center',
                                isLight ? 'text-white/60' : 'text-[#16151466]'
                            )}
                        >
                            {bottomContent ?? 'Free to start · No credit card · First results in minutes'}
                        </div>
                        <div className="flex-1 flex justify-end">
                            {!videoCardDismissed && (
                                <div className="flex items-stretch rounded-xl bg-white/40 backdrop-blur-md ring-1 ring-black/8 shadow-lg overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setVideoModalOpen(true)}
                                            className="relative w-[88px] shrink-0 overflow-hidden group cursor-pointer"
                                        >
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={`https://img.youtube.com/vi/${AGENT_INTRO_VIDEO_ID}/mqdefault.jpg`}
                                                alt="Agent introduction video"
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                                                    <Play className="h-2.5 w-2.5 text-stone-800 ml-px" />
                                                </div>
                                            </div>
                                        </button>

                                        <div className="flex items-center gap-2 pl-3 pr-2 py-2">
                                            <div className="min-w-0">
                                                <div className="text-[11px] font-medium text-black/70 leading-tight">
                                                    Our launch video
                                                </div>
                                                <div className="text-[10px] text-black/40 leading-tight mt-0.5">
                                                    Learn how our agent works
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={dismissVideoCard}
                                                className="shrink-0 w-5 h-5 rounded-full bg-black/8 hover:bg-black/12 flex items-center justify-center transition-colors"
                                                aria-label="Dismiss"
                                            >
                                                <X className="h-3 w-3 text-black/40" />
                                            </button>
                                        </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Intro video card — mobile: inline pill below content */}
                    {!videoCardDismissed && (
                        <div className="relative z-20 flex md:hidden items-center justify-center mx-auto mt-4 mb-2 px-5">
                                <div className="flex items-center gap-2.5 rounded-full bg-white/40 backdrop-blur-md ring-1 ring-black/8 shadow-sm pl-1 pr-1 py-1">
                                    <button
                                        type="button"
                                        onClick={() => setVideoModalOpen(true)}
                                        className="flex items-center gap-2.5 pr-1"
                                    >
                                        <div className="relative w-8 h-8 shrink-0 rounded-full overflow-hidden">
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={`https://img.youtube.com/vi/${AGENT_INTRO_VIDEO_ID}/mqdefault.jpg`}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                <Play className="h-2.5 w-2.5 text-white ml-px" />
                                            </div>
                                        </div>
                                        <span className="text-[12px] font-medium text-black/60">Our launch video</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={dismissVideoCard}
                                        className="shrink-0 w-6 h-6 rounded-full bg-black/8 hover:bg-black/12 flex items-center justify-center transition-colors"
                                        aria-label="Dismiss"
                                    >
                                        <X className="h-3 w-3 text-black/40" />
                                    </button>
                                </div>
                        </div>
                    )}
                </div>
            </div>

            <AgentHomeEmptyYoutubeVideoModal
                open={videoModalOpen}
                onOpenChange={setVideoModalOpen}
                videoId={AGENT_INTRO_VIDEO_ID}
                title="Agent introduction video"
            />
        </section>
    )
}
